import { Category } from "./Category.js";
import type { LocalizedString, LocalizedStringArray } from "./Locale.js";

export enum CardIcon {
	Pig = "Pig",
	Rabbit = "Rabbit",
	Magnifier = "Magnifier",
	Home = "Home",
}

export interface CardTexts {
	icon: CardIcon;
	title: LocalizedString;
	points: LocalizedString[];
}

export interface CategoryTexts {
	headline: LocalizedString;
	subheadline: LocalizedString;
	texts: LocalizedStringArray;
	cards: CardTexts[];
}

export const categoryTexts: Record<Category, CategoryTexts> = {
	[Category.Racks]: {
		headline: {
			cs: "Vertikální zakladače Compact",
			en: "Racks",
		},
		subheadline: {
			cs: "Úspora místa, času a pracovních sil",
			en: "Space, time, and labor savings",
		},
		texts: {
			cs: [
				"Vertikální zakladače (také označované jako věže nebo výtahové skladovací systémy) skladují zboží ve velké výšce bezpečně a zcela pod kontrolou. Tato technologie zajišťuje optimální využití prostoru, umožňuje úspory 70 až 90 % plochy v porovnání se standardním policovým nebo paletovým regálem a výrazné zrychlení toku materiálu.",
				"Naše věžové skladovací systémy, dodávané na klíč potřebám zákazníků, přinášejí viditelný efekt okamžitě po uvedení do provozu ve skladu. Automatický výtahový systém je jednoduchý, jeho obsluha je pohodlná a nevyžaduje časově náročnou údržbu. Umožňuje snížit pracovní náklady, udržuje efektivní přehled o položkách na skladě a přitom zabírá mnohem méně plochy než tradiční řešení. Při správné integraci dokáží vertikální zakladače výrazně zrychlit tok materiálu a jsou tak ideálním modulárním řešením například pro e-commerce.",
				"Návratnost investice do věží VertiFlex je rychlejší, než by se dalo očekávat. Většina projektů počítá s návratností 12 – 24 měsíců.",
			],
			en: [
				"Compact Vertical Storage Lift stores your articles vertically, securely and in a controlled way. Each Vertical Storage Lift is customised to make the optimum use of the space. You save 70-90% of the floor space compared to standard shelving and pallet racks.",
				"Our Racks, tailored to the customer‘s needs, deliver visible results immediately after commissioning in the warehouse.",
				"The automatic vertical storage lift is simple, convenient to operate and does not require time-consuming maintenance. It allows you to reduce labor costs, helps maintain order in the warehouse and at the same time takes up much less space than traditional solutions. With the right application, it can greatly speed up the flow of material, making it an ideal modular solution for e-commerce.",
				"The return on investment for Vertiflex Racks is faster than expected. Often within a year or two. The automatic vertical storage lift pays off!",
			],
		},
		cards: [
			{
				icon: CardIcon.Pig,
				title: {
					cs: "Úspora nákladů",
					en: "Cost savings",
				},
				points: [
					{
						cs: "Nižší provozní náklady",
						en: "Lower operating costs",
					},
					{
						cs: "Rychlá návratnost investice",
						en: "Quick return on investment",
					},
					{
						cs: "Větší skladová plocha",
						en: "Larger storage area",
					},
					{
						cs: "Rychlá montáž a uvedení do provozu",
						en: "Quick assembly and commissioning",
					},
					{
						cs: "Lepší kontrola uloženého zboží",
						en: "Better control of stored goods",
					},
					{
						cs: "Snížení reklamačních nákladů",
						en: "Reduction of complaint costs",
					},
					{
						cs: "Snížení pracovních nákladů",
						en: "Reduction of labor costs",
					},
				],
			},
			{
				icon: CardIcon.Rabbit,
				title: {
					cs: "Zrychlení procesů",
					en: "Process acceleration",
				},
				points: [
					{
						cs: "Kratší časy pro vychystání",
						en: "Shorter picking times",
					},
					{
						cs: "Doprava materiálu v prostojích",
						en: "Material transport in standstills",
					},
					{
						cs: "Rychlejší kompletace",
						en: "Faster completion",
					},
					{
						cs: "Vyšší produktivita skladu",
						en: "Higher warehouse productivity",
					},
					{
						cs: "Kratší pochůzky operátorů",
						en: "Shorter operator walks",
					},
				],
			},
			{
				icon: CardIcon.Magnifier,
				title: {
					cs: "Lepší využití zdrojů",
					en: "Better resource utilization",
				},
				points: [
					{
						cs: "Zlepšení kvality obsluhy",
						en: "Improving service quality",
					},
					{
						cs: "Větší přesnost dodávek",
						en: "Greater delivery accuracy",
					},
					{
						cs: "Méně chyb při kompletaci",
						en: "Fewer errors in completion",
					},
					{
						cs: "Kompletace typu k člověku",
						en: "Completion type to human",
					},
					{
						cs: "Větší možnosti sledování zboží",
						en: "Greater tracking options",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší pracovní prostředí",
					en: "Better working environment",
				},
				points: [
					{
						cs: "Krátký čas na proškolení",
						en: "Short training time",
					},
					{
						cs: "Lepší ergonomie práce",
						en: "Better ergonomics of work",
					},
					{
						cs: "Méně absencí z důvodu nemoci",
						en: "Less sick leave",
					},
					{
						cs: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						en: "Completion accessories facilitating the search for goods",
					},
					{
						cs: "Menší riziko zraňení a úrazu",
						en: "Less risk of injury and accident",
					},
				],
			},
		],
	},
	[Category.Cranes]: {
		headline: {
			cs: "Zakladače a shuttle systémy",
			en: "Cranes and shuttle systems",
		},
		subheadline: {
			cs: "Automatizované skladovací systémy pro zvýšení efektivity a kvality ve skladech",
			en: "Automated storage and retrieval systems to increase efficiency and quality in warehouses",
		},
		texts: {
			cs: [
				"Plně automatizované systémy zvyšují kvalitu, flexibilitu a efektivitu skladu. Nabízíme širokou škálu technologií pro automatizované skladování a vychystávání palet a menšího zboží, jako jsou kartony nebo přepravky.",
				"Automatizovaná skladovací řešení musí být škálovatelná a zároveň maximalizovat využití prostoru a mít odpovídající výkon. Zásadní je také být dostatečně flexibilní, aby bylo možné reagovat na měnící se požadavky trhu a skladbu uloženého sortimentu. Prostor vás stojí peníze! Proto vždy najdeme nejefektivnější řešení s nejlepším poměrem ceny a výkonu, transparentními procesy a největší flexibilitou. Na měnící se podmínky je tak možné kdykoliv reagovat úpravou dostupných kapacit, když je třeba navýšit propustnost skladu.",
				"Automatizovaná řešení pro zásilky a kusové zboží – Dynamické, prostorově úsporné a flexibilní řešení pro skladování drobných dílů, ať už v přepravkách, kartonech nebo kontejnerech! Automatický miniload systém je určen pro efektivní skladování malých a středně velkých položek s vysokou propustností. Tornado miniload a CycloneCarrier shuttle systém jsou ideální kombinací pro rychlý a efektivní provoz.",
				"Automatizovaná řešení pro palety a těžké náklady – Efektivní řešení pro těžké a objemné zboží, které je třeba skladovat rychle, bezpečně a s optimálním využitím prostoru! Automatizovaný paletový sklad lze použít univerzálněji než jakoukoli jinou skladovací technologii. Ať už skladujete v teplotách od -30 °C do +45 °C nebo je váš sklad vysoký 6 až 45 metrů, jsou PowerStore a Vectura ideálním řešením pro rychlý, bezpečný a zdrojově efektivní tok materiálu a skladování zboží až do celkové hmotnosti 3 500 kilogramů.",
			],
			en: [
				"Fully automated systems increase the quality, flexibility and efficiency of the warehouse. We offer a wide range of technologies for automated storage and picking of pallets and smaller goods such as cartons or crates.",
				"Automated warehouse solutions must be scalable while maximizing space utilization and having adequate performance. It is also essential to be flexible enough to respond to changing market demands and the mix of stored product. Space costs you money! That's why we always find the most efficient solution with the best value for money, transparent processes and the greatest flexibility. You can react to changing conditions by adjusting the available capacity at any time when you need to increase your warehouse throughput.",
				"Automated solutions for small loads – A dynamic, space-saving and flexible solution for storing small parts, whether in crates, cartons or containers, the automated miniload system is designed for efficient storage of small and medium-sized items with high throughput. The Tornado miniload and CycloneCarrier shuttle system are the ideal combination for fast and efficient operation.",
				"Automated solutions for pallets and heavy loads – An efficient solutions for heavy and bulky goods that need to be stored quickly, safely and with optimal use of space. Automated pallet storage can be used more universally than any other storage technology. Whether you store in temperatures from -30°C to +45°C or your warehouse is six or 45 metres high at most, PowerStore and Vectura are the ideal combination for fast, safe and resource-efficient material flow and storage of goods up to a total weight of 3,500 kilograms.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					cs: "Úspora nákladů",
					en: "Cost savings",
				},
				points: [
					{
						cs: "Nižší provozní náklady",
						en: "Lower operating costs",
					},
					{
						cs: "Rychlá návratnost investice",
						en: "Quick return on investment",
					},
					{
						cs: "Větší skladová plocha",
						en: "Larger storage area",
					},
					{
						cs: "Rychlá montáž a uvedení do provozu",
						en: "Quick assembly and commissioning",
					},
					{
						cs: "Lepší kontrola uloženého zboží",
						en: "Better control of stored goods",
					},
					{
						cs: "Snížení reklamačních nákladů",
						en: "Reduction of complaint costs",
					},
					{
						cs: "Snížení pracovních nákladů",
						en: "Reduction of labor costs",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Zrychlení procesů",
					en: "Process acceleration",
				},
				points: [
					{
						cs: "Kratší časy pro vychystání",
						en: "Shorter picking times",
					},
					{
						cs: "Doprava materiálu v prostojích",
						en: "Material transport in standstills",
					},
					{
						cs: "Rychlejší kompletace",
						en: "Faster completion",
					},
					{
						cs: "Vyšší produktivita skladu",
						en: "Higher warehouse productivity",
					},
					{
						cs: "Kratší pochůzky operátorů",
						en: "Shorter operator walks",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší využití zdrojů",
					en: "Better resource utilization",
				},
				points: [
					{
						cs: "Zlepšení kvality obsluhy",
						en: "Improving service quality",
					},
					{
						cs: "Větší přesnost dodávek",
						en: "Greater delivery accuracy",
					},
					{
						cs: "Méně chyb při kompletaci",
						en: "Fewer errors in completion",
					},
					{
						cs: "Kompletace typu k člověku",
						en: "Completion type to human",
					},
					{
						cs: "Větší možnosti sledování zboží",
						en: "Greater tracking options",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší pracovní prostředí",
					en: "Better working environment",
				},
				points: [
					{
						cs: "Krátký čas na proškolení",
						en: "Short training time",
					},
					{
						cs: "Lepší ergonomie práce",
						en: "Better ergonomics of work",
					},
					{
						cs: "Méně absencí z důvodu nemoci",
						en: "Less sick leave",
					},
					{
						cs: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						en: "Completion accessories facilitating the search for goods",
					},
					{
						cs: "Menší riziko zraňení a úrazu",
						en: "Less risk of injury and accident",
					},
				],
			},
		],
	},
	[Category.Robots]: {
		headline: {
			cs: "Roboti Geek+",
			en: "Geek+ Robots",
		},
		subheadline: {
			cs: "Inovativní robotická technologie pro automatizaci skladů",
			en: "Innovative robotic technology for warehouse automation",
		},
		texts: {
			cs: [
				"Naše řešení využívající robotů Geek+ představuje špičkovou technologickou platformu zaměřenou na inteligentní robotická řešení, která transformují skladové operace. Tato pokročilá technologie využívá moderní roboty navržené pro různé činnosti, včetně vychystávání, třídění, dopravy a skladování zboží.",
				"Díky použití umělé inteligence a pokročilých algoritmů roboty zajišťují vysokou úroveň přesnosti a efektivity. Umožňují rychlejší a flexibilnější zpracování objednávek, čímž snižují provozní náklady a zvyšují produktivitu skladu.",
				"Celý systém robotů je navíc modulární a škálovatelný, což umožňuje snadné přizpůsobení měnícím se potřebám podnikání. Je ideální volbou pro sklady, které chtějí optimalizovat své procesy a udržet krok s rychle se měnícím trhem. S roboty získává vaše logistika výhodu ve flexibilitě a rychlosti, a to bez ohledu na velikost skladu nebo odvětví, ve kterém působíte.",
			],
			en: [
				"Our Geek+ robot solution is a cutting-edge technology platform focused on intelligent robotic solutions that transform warehouse operations. This advanced technology uses state-of-the-art robots designed for a variety of activities including picking, sorting, transporting and storing goods.",
				"Using artificial intelligence and advanced algorithms, the robots ensure high levels of accuracy and efficiency. They enable faster and more flexible order processing, reducing operating costs and increasing warehouse productivity.",
				"In addition, the entire robot system is modular and scalable, making it easy to adapt to changing business needs. It is the ideal choice for warehouses that want to optimize their processes and keep up with a rapidly changing market. With robots, your logistics department gains an advantage in flexibility and speed, regardless of the size of your warehouse or the industry you operate in.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					cs: "Úspora nákladů",
					en: "Cost savings",
				},
				points: [
					{
						cs: "Nižší provozní náklady",
						en: "Lower operating costs",
					},
					{
						cs: "Rychlá návratnost investice",
						en: "Quick return on investment",
					},
					{
						cs: "Větší skladová plocha",
						en: "Larger storage area",
					},
					{
						cs: "Rychlá montáž a uvedení do provozu",
						en: "Quick assembly and commissioning",
					},
					{
						cs: "Lepší kontrola uloženého zboží",
						en: "Better control of stored goods",
					},
					{
						cs: "Snížení reklamačních nákladů",
						en: "Reduction of complaint costs",
					},
					{
						cs: "Snížení pracovních nákladů",
						en: "Reduction of labor costs",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Zrychlení procesů",
					en: "Process acceleration",
				},
				points: [
					{
						cs: "Kratší časy pro vychystání",
						en: "Shorter picking times",
					},
					{
						cs: "Doprava materiálu v prostojích",
						en: "Material transport in standstills",
					},
					{
						cs: "Rychlejší kompletace",
						en: "Faster completion",
					},
					{
						cs: "Vyšší produktivita skladu",
						en: "Higher warehouse productivity",
					},
					{
						cs: "Kratší pochůzky operátorů",
						en: "Shorter operator walks",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší využití zdrojů",
					en: "Better resource utilization",
				},
				points: [
					{
						cs: "Zlepšení kvality obsluhy",
						en: "Improving service quality",
					},
					{
						cs: "Větší přesnost dodávek",
						en: "Greater delivery accuracy",
					},
					{
						cs: "Méně chyb při kompletaci",
						en: "Fewer errors in completion",
					},
					{
						cs: "Kompletace typu k člověku",
						en: "Completion type to human",
					},
					{
						cs: "Větší možnosti sledování zboží",
						en: "Greater tracking options",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší pracovní prostředí",
					en: "Better working environment",
				},
				points: [
					{
						cs: "Krátký čas na proškolení",
						en: "Short training time",
					},
					{
						cs: "Lepší ergonomie práce",
						en: "Better ergonomics of work",
					},
					{
						cs: "Méně absencí z důvodu nemoci",
						en: "Less sick leave",
					},
					{
						cs: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						en: "Completion accessories facilitating the search for goods",
					},
					{
						cs: "Menší riziko zraňení a úrazu",
						en: "Less risk of injury and accident",
					},
				],
			},
		],
	},
	[Category.VertinodeAi]: {
		headline: {
			cs: "VertiNode Ai",
			en: "VertiNode Ai",
		},
		subheadline: {
			cs: "Speciální software VertiNode Ai pro automatizovanou logistiku",
			en: "Special software VertiNode Ai for automated logistics",
		},
		texts: {
			cs: [
				"Software pro automatizovanou logistiku s nejnovější technologií umělé inteligence a kompatibilní s většinou skladovacích systémů",
				"VertiNode Ai má mnoho použití, ale vynikne především v prostředí e-commerce. Komplexní řešení dovolí integrovat výstupy z AI přímo do procesů společnosti. ERP systém se může od VertiNode Ai dozvědět okamžitě po předání zakázky k vyskladnění cílovou vyskladňovací pozici a očekávaný čas zpracování, tyto informace lze využít pro okamžitou komunikaci klientovi v procesu zpracování objednávky. Stavy zakázky jsou přístupné přes API rozhraní. ERP systém (a potažmo klient) tak může kdykoliv zjistit, kdy přibližně dojde k expedici, že expedice byla započata a úspěšně dokončena nebo zdržena, tracking ID zakázky pro okamžitou konfirmaci přepravní společnosti, to vše za účelem zvýšení transparentnosti a rychlosti dodávky.",
			],
			en: [
				"Software for automated logistics with the latest artificial intelligence technology and compatible with most storage systems",
				"VertiNode Ai has many uses, but excels especially in the e-commerce environment. The comprehensive solution allows the integration of AI outputs directly into the company's processes. The ERP system can learn from VertiNode Ai immediately after the order is handed over for picking to the target picking position and the expected processing time, this information can be used for instant communication with the client in the order processing process. Order statuses are accessible via the API interface. The ERP system (and consequently the client) can find out at any time when the shipment will be dispatched, that the shipment has started and successfully completed or delayed, the tracking ID of the order for immediate confirmation by the carrier, all to increase transparency and delivery speed.",
			],
		},
		cards: [
			{
				icon: CardIcon.Home,
				title: {
					cs: "Úspora nákladů",
					en: "Cost savings",
				},
				points: [
					{
						cs: "Nižší provozní náklady",
						en: "Lower operating costs",
					},
					{
						cs: "Rychlá návratnost investice",
						en: "Quick return on investment",
					},
					{
						cs: "Větší skladová plocha",
						en: "Larger storage area",
					},
					{
						cs: "Rychlá montáž a uvedení do provozu",
						en: "Quick assembly and commissioning",
					},
					{
						cs: "Lepší kontrola uloženého zboží",
						en: "Better control of stored goods",
					},
					{
						cs: "Snížení reklamačních nákladů",
						en: "Reduction of complaint costs",
					},
					{
						cs: "Snížení pracovních nákladů",
						en: "Reduction of labor costs",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Zrychlení procesů",
					en: "Process acceleration",
				},
				points: [
					{
						cs: "Kratší časy pro vychystání",
						en: "Shorter picking times",
					},
					{
						cs: "Doprava materiálu v prostojích",
						en: "Material transport in standstills",
					},
					{
						cs: "Rychlejší kompletace",
						en: "Faster completion",
					},
					{
						cs: "Vyšší produktivita skladu",
						en: "Higher warehouse productivity",
					},
					{
						cs: "Kratší pochůzky operátorů",
						en: "Shorter operator walks",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší využití zdrojů",
					en: "Better resource utilization",
				},
				points: [
					{
						cs: "Zlepšení kvality obsluhy",
						en: "Improving service quality",
					},
					{
						cs: "Větší přesnost dodávek",
						en: "Greater delivery accuracy",
					},
					{
						cs: "Méně chyb při kompletaci",
						en: "Fewer errors in completion",
					},
					{
						cs: "Kompletace typu k člověku",
						en: "Completion type to human",
					},
					{
						cs: "Větší možnosti sledování zboží",
						en: "Greater tracking options",
					},
				],
			},
			{
				icon: CardIcon.Home,
				title: {
					cs: "Lepší pracovní prostředí",
					en: "Better working environment",
				},
				points: [
					{
						cs: "Krátký čas na proškolení",
						en: "Short training time",
					},
					{
						cs: "Lepší ergonomie práce",
						en: "Better ergonomics of work",
					},
					{
						cs: "Méně absencí z důvodu nemoci",
						en: "Less sick leave",
					},
					{
						cs: "Příslušenství pro kompletaci usnadňující vyhledávání zboží",
						en: "Completion accessories facilitating the search for goods",
					},
					{
						cs: "Menší riziko zraňení a úrazu",
						en: "Less risk of injury and accident",
					},
				],
			},
		],
	},
};
